import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";

import "./chatPage.css";
import { WidgetGrid } from "../components/WidgetGrid";
import { DataVisualizer } from "../components/DataVisualizer";
import { getAccessToken, getActiveCollectionId, getConnectionId, getUserId, saveActiveCollectionId, saveConnectionId } from "../utils/storage";
import PromptHistoryService from "../services/promptHistoryService";
import { PromptHistoryPopup } from "../components/PromptHistoryPopup";
import SettingsPopup from "../components/SettingsPopup";
import Toast from "../components/Toast";
import ThemeToggle from "../components/ThemeToggle";
import StorePerformanceDashboard, { StorePerformanceContext, StoreReportTab } from "../components/StorePerformanceDashboard";
import LMSReconDashboard, { lmsReconReportTabs, LMSReconContext, LMSReconReportKey } from "../components/LMSReconDashboard";
import LMSMasterDashboard, { lmsMasterReportTabs, LMSMasterContext, LMSMasterReportKey } from "../components/LMSMasterDashboard";
import DebtReviewDashboard, { debtReviewReportTabs, DebtReviewContext, DebtReviewReportKey } from "../components/DebtReviewDashboard";
import VacancyReportDashboard, { vacancyReportTabs, VacancyReportContext, VacancyReportTab } from "../components/VacancyReportDashboard";
import { fetchUserConnections, submitBatchQuery, connectBatchWebSocket, fetchRecentResponses, removeRecentResponse, fetchEmbeddingStatus, connectEmbeddingWebSocket, cancelBatchQuery, setConnectionActive } from "../services/databaseService";
import type { EmbeddingStatusMessage } from "../services/databaseService";
import { QueryResultCard, QueryCardData } from "../components/QueryResultCard";
import { QueryExpandModal } from "../components/QueryExpandModal";
import { RetryModal } from "../components/RetryModal";
import { Collection } from "../types/collection";
import { createCollection, fetchCollections } from "../services/collectionService";
import {
  enableFullPermissionPreview,
  fetchLiveDashboardSession,
  getStoredLiveAllowedModules,
  getStoredLivePermissions,
  isLocalPermissionPreviewEnabled,
  LiveDashboardModule,
} from "../services/liveDashboardAuthService";
import { authFetch, getApiUrl } from "../utils/api";

type BuiltInReportContext = StorePerformanceContext | LMSReconContext | LMSMasterContext | DebtReviewContext | VacancyReportContext;
const ASK_MORE_CONTEXT_SAMPLE_LIMIT = 10;

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

interface QueryResult {
  id: string;
  question: string;
  response: any;
  timestamp: Date;
}

const storeReportTabs: Array<[StoreReportTab, string]> = [
  ['store-performance', 'Store Performance'],
  ['segment-report', 'Segment Report'],
  ['forecast-report', 'Forecast Report'],
  ['new-stores-report', 'New Stores Report'],
];

const storeReportPermissionByTab: Record<StoreReportTab, string> = {
  'store-performance': 'store_performance.report:view',
  'segment-report': 'store_performance.segment:view',
  'forecast-report': 'forecast.report:view',
  'new-stores-report': 'new_stores.report:view',
};

const lmsMasterPermissionByTab: Record<LMSMasterReportKey, string> = {
  'branch-report': 'lms_master.branch_report:view',
  'store-marketing-attributes': 'lms_master.store_marketing_attributes:view',
  'store-attributes': 'lms_master.store_attributes:view',
};

const debtReviewPermissionByTab: Record<DebtReviewReportKey, string> = {
  'account-access': 'debt_review.account_access:view',
  'application-users': 'debt_review.application_users:view',
};

const vacancyReportPermissionByTab: Record<VacancyReportTab, string> = {
  'planned-vs-actual': 'vacancy_report.planned_vs_actual:view',
  'vacancies': 'vacancy_report.vacancies:view',
  'overstaffing': 'vacancy_report.overstaffing:view',
};

const lmsReconPermissionByTab: Record<LMSReconReportKey, string> = {
  'progress-report': 'lms_recon.progress_report:view',
  'rental-recon-summary': 'lms_recon.rental_recon_summary:view',
  'utilities-vs-rent': 'lms_recon.utilities_vs_rent:view',
  'invoiced-vs-paid-rent': 'lms_recon.invoiced_vs_paid_rent:view',
  'invoiced-vs-paid-utilities': 'lms_recon.invoiced_vs_paid_utilities:view',
  'contractual-charges': 'lms_recon.contractual_charges:view',
  'variable-charges': 'lms_recon.variable_charges:view',
  'escalations-rent': 'lms_recon.escalations_rent:view',
  'escalations-other': 'lms_recon.escalations_other:view',
  'growth-rent': 'lms_recon.growth_rent:view',
  'growth-utilities': 'lms_recon.growth_utilities:view',
  'total-rate-per-sqm': 'lms_recon.total_rate_per_sqm:view',
  'rental-monthend-batch-report': 'lms_recon.rental_monthend_batch_report:view',
  'rental-arrears-batch-report': 'lms_recon.rental_arrears_batch_report:view',
  'municipal-monthend-batch-report': 'lms_recon.municipal_monthend_batch_report:view',
  'municipal-arrears-batch-report': 'lms_recon.municipal_arrears_batch_report:view',
  'transaction-report': 'lms_recon.transaction_report:view',
};

const normalizePermissionLabel = (value: string): string => value.trim().toLowerCase();

// Eagerly refresh localStorage so the useState initializer below reads the
// current module order/labels, not a stale cached value from a previous session.
if (isLocalPermissionPreviewEnabled()) {
  enableFullPermissionPreview();
}

// Local overrides for module display — applied on top of whatever the server
// returns so that label and ordering changes take effect without a backend deploy.
const MODULE_DISPLAY: Record<string, { label: string; order: number }> = {
  'lms_recon_analysis': { label: 'LMS Recon Analysis',     order: 1 },
  'lms_master_report':  { label: 'LMS Master Report',       order: 2 },
  'store_performance':  { label: 'LMS Store Analytics V2',  order: 3 },
  'debt_review':        { label: 'Debt Review',              order: 4 },
  'vacancy_report':     { label: 'Vacancy Report',            order: 5 },
};

const liveModulesToCollections = (modules: LiveDashboardModule[]): Collection[] => {
  const supportedModuleKeys = new Set(Object.keys(MODULE_DISPLAY));
  return modules
    .filter((module) => supportedModuleKeys.has(module.moduleKey))
    .sort((a, b) => (MODULE_DISPLAY[a.moduleKey]?.order ?? 99) - (MODULE_DISPLAY[b.moduleKey]?.order ?? 99))
    .map((module) => ({
      id: `live-module-${module.moduleKey}`,
      user_id: 0,
      connection_id: 0,
      name: MODULE_DISPLAY[module.moduleKey]?.label ?? module.label,
      display_order: MODULE_DISPLAY[module.moduleKey]?.order ?? 99,
      created_at: new Date(0).toISOString(),
      updated_at: new Date(0).toISOString(),
    }));
};

const DASHBOARD_SESSION_STATE_KEY = 'citywide.chat.dashboardState';

interface DashboardSessionState {
  activeCollectionId?: string | null;
  activeStoreReportTab?: StoreReportTab;
  activeReconReportTab?: LMSReconReportKey;
  activeMasterReportTab?: LMSMasterReportKey;
  activeDebtReviewReportTab?: DebtReviewReportKey;
  activeVacancyReportTab?: VacancyReportTab;
}

const readDashboardSessionState = (): DashboardSessionState => {
  if (typeof window === 'undefined') {
    return {};
  }
  try {
    const rawState = window.sessionStorage.getItem(DASHBOARD_SESSION_STATE_KEY);
    return rawState ? JSON.parse(rawState) : {};
  } catch {
    return {};
  }
};

const updateDashboardSessionState = (partialState: DashboardSessionState) => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const currentState = readDashboardSessionState();
    window.sessionStorage.setItem(
      DASHBOARD_SESSION_STATE_KEY,
      JSON.stringify({ ...currentState, ...partialState }),
    );
  } catch {
    // Session restore is a convenience; ignore storage failures.
  }
};

const getStoredDashboardCollectionId = (): string | null =>
  readDashboardSessionState().activeCollectionId || null;

const getStoredStoreReportTab = (): StoreReportTab => {
  const storedTab = readDashboardSessionState().activeStoreReportTab;
  return storeReportTabs.some(([key]) => key === storedTab) ? storedTab as StoreReportTab : 'store-performance';
};

const getStoredReconReportTab = (): LMSReconReportKey => {
  const storedTab = readDashboardSessionState().activeReconReportTab;
  return lmsReconReportTabs.some(([key]) => key === storedTab) ? storedTab as LMSReconReportKey : 'progress-report';
};

const getStoredMasterReportTab = (): LMSMasterReportKey => {
  const storedTab = readDashboardSessionState().activeMasterReportTab;
  return lmsMasterReportTabs.some(([key]) => key === storedTab) ? storedTab as LMSMasterReportKey : 'branch-report';
};

const getStoredDebtReviewReportTab = (): DebtReviewReportKey => {
  const storedTab = readDashboardSessionState().activeDebtReviewReportTab;
  return debtReviewReportTabs.some(([key]) => key === storedTab) ? storedTab as DebtReviewReportKey : 'account-access';
};

const getStoredVacancyReportTab = (): VacancyReportTab => {
  const storedTab = readDashboardSessionState().activeVacancyReportTab;
  return vacancyReportTabs.some(([key]) => key === storedTab) ? storedTab as VacancyReportTab : 'planned-vs-actual';
};

const ChatPage = () => {
  const location = useLocation();
  const [query, setQuery] = useState("");
  const [comment, setComment] = useState("");
  const [showCommentPopup, setShowCommentPopup] = useState(false);
  const [results, setResults] = useState<QueryResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const sessionCreatedRef = useRef(false);
  const sessionCreationPromiseRef = useRef<Promise<string | null> | null>(null);
  const sessionScopeRef = useRef<{ connectionId: string; collectionId: string } | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [widgetName, setWidgetName] = useState("");
  const [saveCollectionId, setSaveCollectionId] = useState<string | null>(null);
  const [saveReportTab, setSaveReportTab] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [askMorePopup, setAskMorePopup] = useState<{ open: boolean; contextQuery: string; contextData: any } | null>(null);
  const [isSubmittingAskMore, setIsSubmittingAskMore] = useState(false);
  const [showPromptHistory, setShowPromptHistory] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' | 'info' } | null>(null);
  const userId = getUserId() || ""; // Require auth-derived user id
  const widgetGridRef = useRef<{ refresh: () => Promise<void> }>(null);
  const [showAskMoreModal, setShowAskMoreModal] = useState(false);
  const [askMoreQuery, setAskMoreQuery] = useState("");
  const [isAskingMore, setIsAskingMore] = useState(false);
  const [activeConnectionName, setActiveConnectionName] = useState<string | null>(null);
  const [availableConnections, setAvailableConnections] = useState<any[]>([]);
  const [hasValidConnection, setHasValidConnection] = useState<boolean>(true);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [activeCollectionId, setActiveCollectionId] = useState<string | null>(() => readDashboardSessionState().activeCollectionId || null);
  const [activeStoreReportTab, setActiveStoreReportTab] = useState<StoreReportTab>(() => getStoredStoreReportTab());
  const [activeReconReportTab, setActiveReconReportTab] = useState<LMSReconReportKey>(() => getStoredReconReportTab());
  const [activeMasterReportTab, setActiveMasterReportTab] = useState<LMSMasterReportKey>(() => getStoredMasterReportTab());
  const [activeDebtReviewReportTab, setActiveDebtReviewReportTab] = useState<DebtReviewReportKey>(() => getStoredDebtReviewReportTab());
  const [activeVacancyReportTab, setActiveVacancyReportTab] = useState<VacancyReportTab>(() => getStoredVacancyReportTab());
  const [builtInReportContext, setBuiltInReportContext] = useState<BuiltInReportContext | null>(null);
  const [isLoadingCollections, setIsLoadingCollections] = useState(false);
  const [showCreateCollectionModal, setShowCreateCollectionModal] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState("");
  const [isCreatingCollection, setIsCreatingCollection] = useState(false);
  const [liveAllowedModules, setLiveAllowedModules] = useState<LiveDashboardModule[] | null>(() => getStoredLiveAllowedModules());
  const [livePermissions, setLivePermissions] = useState<string[] | null>(() => getStoredLivePermissions());

  // Embedding status state
  const [embeddingStatus, setEmbeddingStatus] = useState<string | null>(null);
  const [embeddingError, setEmbeddingError] = useState<string | null>(null);

  const embeddingWsRef = useRef<WebSocket | null>(null);

  // Batch query states
  const [queryStatuses, setQueryStatuses] = useState<QueryCardData[]>([]);
  const wsRef = useRef<WebSocket | null>(null);

  // Stable per-tab session key for pagination/context window — resets on page refresh
  const [pageSessionKey] = useState<string>(() => crypto.randomUUID());

  // Retry modal state
  const [showRetryModal, setShowRetryModal] = useState(false);
  const [retryQuery, setRetryQuery] = useState<QueryCardData | null>(null);

  // Ask More context - for query responses (showAskMoreModal, askMoreQuery, isAskingMore already exist above)
  const [askMoreContext, setAskMoreContext] = useState<{ process_id: string; question: string } | null>(null);

  // Expand modal state
  const [expandedQuery, setExpandedQuery] = useState<QueryCardData | null>(null);

  // Embed script modal state
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [embedScript, setEmbedScript] = useState("");
  const [isGeneratingEmbed, setIsGeneratingEmbed] = useState(false);
  const [embedCopied, setEmbedCopied] = useState(false);
  const hasLivePermissionProfile = liveAllowedModules !== null;
  const livePermissionSet = useMemo(() => {
    const permissionKeys = new Set<string>(livePermissions || []);
    (liveAllowedModules || []).forEach((module) => {
      module.reports.forEach((report) => {
        (report.permissionKeys || []).forEach((permissionKey) => permissionKeys.add(permissionKey));
      });
    });
    return permissionKeys;
  }, [liveAllowedModules, livePermissions]);

  const liveModuleKeys = useMemo(
    () => new Set((liveAllowedModules || []).map((module) => module.moduleKey)),
    [liveAllowedModules],
  );

  const allowedLiveLabels = useMemo(() => {
    const labels = new Set<string>();
    (liveAllowedModules || []).forEach((module) => {
      labels.add(normalizePermissionLabel(module.moduleKey));
      labels.add(normalizePermissionLabel(module.label));
      module.reports.forEach((report) => {
        labels.add(normalizePermissionLabel(report.reportKey));
        labels.add(normalizePermissionLabel(report.label));
      });
    });
    return labels;
  }, [liveAllowedModules]);

  const hasAnyMappedPermission = useCallback((permissions: string[]) => {
    if (!hasLivePermissionProfile) return true;
    return permissions.some((permission) => livePermissionSet.has(permission));
  }, [hasLivePermissionProfile, livePermissionSet]);

  const canShowCollection = useCallback((collection: Collection) => {
    if (!hasLivePermissionProfile) {
      return true;
    }

    // Match on stable collection ID (live-module-<moduleKey>) so visibility
    // checks survive label renames. Fall back to name matching for non-live collections.
    if (collection.id === 'live-module-lms_master_report') {
      return liveModuleKeys.has('lms_master_report') || hasAnyMappedPermission(Object.values(lmsMasterPermissionByTab));
    }
    if (collection.id === 'live-module-lms_recon_analysis') {
      return liveModuleKeys.has('lms_recon_analysis') || hasAnyMappedPermission(Object.values(lmsReconPermissionByTab));
    }
    if (collection.id === 'live-module-store_performance') {
      return liveModuleKeys.has('store_performance') || hasAnyMappedPermission(Object.values(storeReportPermissionByTab));
    }
    if (collection.id === 'live-module-debt_review') {
      return liveModuleKeys.has('debt_review') || hasAnyMappedPermission(Object.values(debtReviewPermissionByTab));
    }
    if (collection.id === 'live-module-vacancy_report') {
      return liveModuleKeys.has('vacancy_report') || hasAnyMappedPermission(Object.values(vacancyReportPermissionByTab));
    }

    return allowedLiveLabels.has(normalizePermissionLabel(collection.name));
  }, [allowedLiveLabels, hasAnyMappedPermission, hasLivePermissionProfile, liveModuleKeys]);

  const visibleCollections = useMemo(
    () => collections.filter(canShowCollection),
    [collections, canShowCollection],
  );

  const visibleStoreReportTabs = useMemo(
    () => storeReportTabs.filter(([key]) => !hasLivePermissionProfile || livePermissionSet.has(storeReportPermissionByTab[key])),
    [hasLivePermissionProfile, livePermissionSet],
  );

  const visibleReconReportTabs = useMemo(
    () => lmsReconReportTabs.filter(([key]) => !hasLivePermissionProfile || livePermissionSet.has(lmsReconPermissionByTab[key])),
    [hasLivePermissionProfile, livePermissionSet],
  );
  const visibleMasterReportTabs = useMemo(
    () => lmsMasterReportTabs.filter(([key]) => !hasLivePermissionProfile || livePermissionSet.has(lmsMasterPermissionByTab[key])),
    [hasLivePermissionProfile, livePermissionSet],
  );
  const visibleDebtReviewReportTabs = useMemo(
    () => debtReviewReportTabs.filter(([key]) => !hasLivePermissionProfile || livePermissionSet.has(debtReviewPermissionByTab[key])),
    [hasLivePermissionProfile, livePermissionSet],
  );
  const visibleVacancyReportTabs = useMemo(
    () => vacancyReportTabs.filter(([key]) => !hasLivePermissionProfile || livePermissionSet.has(vacancyReportPermissionByTab[key])),
    [hasLivePermissionProfile, livePermissionSet],
  );

  const activeCollection = visibleCollections.find((item) => item.id === activeCollectionId) || null;
  const activeCollectionName = (activeCollection?.name || "").trim().toLowerCase();
  const isLMSMasterCollection = activeCollectionName === "lms master report";
  const isLMSReconCollection = activeCollectionName === "lms recon analysis";
  const isDebtReviewCollection = activeCollectionName === "debt review";
  const isVacancyReportCollection = activeCollectionName === "vacancy report";
  const isStorePerformanceCollection = activeCollectionId === 'live-module-store_performance';
  const activeBuiltInReportContext =
    isLMSMasterCollection && builtInReportContext?.report_family === 'lms-master'
      ? builtInReportContext
      : isLMSReconCollection && builtInReportContext?.report_family === 'lms-recon'
        ? builtInReportContext
        : isDebtReviewCollection && builtInReportContext?.report_family === 'debt-review'
          ? builtInReportContext
          : isVacancyReportCollection && builtInReportContext?.report_family === 'vacancy-report'
            ? builtInReportContext
            : isStorePerformanceCollection && builtInReportContext?.report_family === 'store-performance'
              ? builtInReportContext
              : null;
  const requestReportContext: BuiltInReportContext | null = activeBuiltInReportContext || (
    isLMSMasterCollection
      ? {
          report_family: 'lms-master',
          active_tab: activeMasterReportTab,
          report_title: lmsMasterReportTabs.find(([key]) => key === activeMasterReportTab)?.[1] || 'LMS Master Report',
          report_view: 'table',
        }
      : isLMSReconCollection
      ? {
          report_family: 'lms-recon',
          active_tab: activeReconReportTab,
          report_title: lmsReconReportTabs.find(([key]) => key === activeReconReportTab)?.[1] || 'LMS Recon Analysis',
          report_view: 'table',
        }
      : isDebtReviewCollection
      ? {
          report_family: 'debt-review',
          active_tab: activeDebtReviewReportTab,
          report_title: debtReviewReportTabs.find(([key]) => key === activeDebtReviewReportTab)?.[1] || 'Debt Review',
          report_view: 'table',
        }
      : isVacancyReportCollection
      ? {
          report_family: 'vacancy-report',
          active_tab: activeVacancyReportTab,
          report_title: vacancyReportTabs.find(([key]) => key === activeVacancyReportTab)?.[1] || 'Vacancy Report',
          report_view: 'table',
        }
      : null
  );

  const getRequestConnectionId = useCallback((): string | null => {
    const storedConnectionId = getConnectionId();
    if (storedConnectionId) {
      return storedConnectionId;
    }
    if (activeCollectionId?.startsWith('live-module-')) {
      return '0';
    }
    return null;
  }, [activeCollectionId]);

  // Returns the active sub-tab for the current live-module collection.
  // Used to auto-scope saved widgets to the exact screen the user is on.
  const getActiveLiveReportTab = useCallback((): string | null => {
    if (activeCollectionId === 'live-module-store_performance') return activeStoreReportTab;
    if (activeCollectionId === 'live-module-lms_recon_analysis') return activeReconReportTab;
    if (activeCollectionId === 'live-module-lms_master_report') return activeMasterReportTab;
    return null;
  }, [activeCollectionId, activeStoreReportTab, activeReconReportTab, activeMasterReportTab]);

  const resetChatSession = useCallback(() => {
    setSessionId(null);
    sessionScopeRef.current = null;
    sessionCreatedRef.current = false;
    sessionCreationPromiseRef.current = null;
  }, []);


  // Note: Removed automatic redirect to database-config
  // Users can access chat page even without database connection configured

  const loadCollections = useCallback(async () => {
    if (liveAllowedModules !== null) {
      const liveCollections = liveModulesToCollections(liveAllowedModules);
      setCollections(liveCollections);
      const sessionCollectionId = getStoredDashboardCollectionId();
      const selected =
        liveCollections.find((item) => item.id === activeCollectionId) ||
        liveCollections.find((item) => item.id === sessionCollectionId) ||
        liveCollections[0] ||
        null;
      setActiveCollectionId(selected?.id || null);
      return;
    }

    const connectionId = getRequestConnectionId();
    if (!connectionId) {
      setCollections([]);
      setActiveCollectionId(null);
      return;
    }

    setIsLoadingCollections(true);
    try {
      const items = await fetchCollections(parseInt(connectionId));
      setCollections(items);
      const sessionCollectionId = getStoredDashboardCollectionId();
      const storedCollectionId = getActiveCollectionId(connectionId);
      const selected =
        items.find((item) => item.id === activeCollectionId) ||
        items.find((item) => item.id === sessionCollectionId) ||
        items.find((item) => item.id === storedCollectionId) ||
        items[0] ||
        null;
      setActiveCollectionId(selected?.id || null);
      if (selected) {
        saveActiveCollectionId(selected.id, connectionId);
      }
    } catch (error) {
      console.error("Error loading collections:", error);
      setCollections([]);
      setActiveCollectionId(null);
      setToast({ message: "Failed to load collections", type: "error" });
    } finally {
      setIsLoadingCollections(false);
    }
  }, [activeCollectionId, getRequestConnectionId, liveAllowedModules]);

  const generateSession = useCallback(async (): Promise<string | null> => {
    if (!activeCollectionId) {
      return null;
    }

    const connectionId = getRequestConnectionId();
    if (!connectionId) {
      return null;
    }

    const existingSessionMatchesScope = Boolean(
      sessionId
      && sessionScopeRef.current?.connectionId === connectionId
      && sessionScopeRef.current?.collectionId === activeCollectionId,
    );
    if (existingSessionMatchesScope) {
      return sessionId;
    }

    if (sessionId) {
      resetChatSession();
    }

    if (sessionCreationPromiseRef.current) {
      return sessionCreationPromiseRef.current;
    }

    const sessionPromise = (async () => {
      sessionCreatedRef.current = true;
      setIsCreatingSession(true);
      try {
        const payload = {
          connection_id: parseInt(connectionId),
          collection_id: activeCollectionId,
        };

        const accessToken = getAccessToken();
        const response = await authFetch(
          getApiUrl('/v1/sessions/create'),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
            },
            body: JSON.stringify(payload),
          }
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setSessionId(data.session_id);
        sessionScopeRef.current = {
          connectionId,
          collectionId: activeCollectionId,
        };
        if (results.length > 0) setResults([]);
        return data.session_id as string;
      } catch (error) {
        console.error("Error creating session:", error);
        resetChatSession();
        return null;
      } finally {
        setIsCreatingSession(false);
        sessionCreationPromiseRef.current = null;
      }
    })();

    sessionCreationPromiseRef.current = sessionPromise;
    return sessionPromise;
  }, [activeCollectionId, getRequestConnectionId, resetChatSession, sessionId, results.length]);

  // Generate session when component mounts
  useEffect(() => {
    loadActiveConnectionName();
    loadCollections();

    // Cleanup WebSocket on unmount
    return () => {
      if (wsRef.current) {
        console.log('[WebSocket] Cleaning up on unmount');
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [loadCollections]);

  useEffect(() => {
    // When running in local permission preview, always rewrite the stored
    // modules from the current code definition so that order or label changes
    // take effect without requiring the user to re-enable the preview.
    if (isLocalPermissionPreviewEnabled()) {
      enableFullPermissionPreview();
    }

    const storedModules = getStoredLiveAllowedModules();
    const storedPermissions = getStoredLivePermissions();
    if (storedModules) {
      setLiveAllowedModules(storedModules);
    }
    if (storedPermissions) {
      setLivePermissions(storedPermissions);
    }

    if (!getAccessToken()) {
      return;
    }

    fetchLiveDashboardSession()
      .then((session) => {
        setLiveAllowedModules(session.allowedModules || []);
        setLivePermissions(session.permissions || []);
      })
      .catch(() => {
        // Normal application logins do not have a live permission profile.
      });
  }, []);

  useEffect(() => {
    if (!hasLivePermissionProfile) {
      return;
    }

    if (visibleCollections.length === 0) {
      if (activeCollectionId) {
        setActiveCollectionId(null);
        setBuiltInReportContext(null);
      }
      return;
    }

    if (!activeCollectionId || !visibleCollections.some((collection) => collection.id === activeCollectionId)) {
      const sessionCollectionId = getStoredDashboardCollectionId();
      const fallback =
        visibleCollections.find((collection) => collection.id === sessionCollectionId) ||
        visibleCollections[0];
      const connectionId = getConnectionId();
      setActiveCollectionId(fallback.id);
      if (connectionId) {
        saveActiveCollectionId(fallback.id, connectionId);
      }
      setBuiltInReportContext(null);
      setQueryStatuses([]);
      setResults([]);
      resetChatSession();
    }
  }, [activeCollectionId, hasLivePermissionProfile, resetChatSession, visibleCollections]);

  useEffect(() => {
    if (activeCollectionId && !sessionId && !isCreatingSession && !sessionCreatedRef.current) {
      generateSession();
    }
  }, [activeCollectionId, sessionId, isCreatingSession, generateSession]);

  useEffect(() => {
    if (activeCollectionId) {
      updateDashboardSessionState({ activeCollectionId });
    }
  }, [activeCollectionId]);

  useEffect(() => {
    updateDashboardSessionState({ activeStoreReportTab });
  }, [activeStoreReportTab]);

  useEffect(() => {
    updateDashboardSessionState({ activeReconReportTab });
  }, [activeReconReportTab]);

  useEffect(() => {
    updateDashboardSessionState({ activeMasterReportTab });
  }, [activeMasterReportTab]);

  useEffect(() => {
    updateDashboardSessionState({ activeDebtReviewReportTab });
  }, [activeDebtReviewReportTab]);

  useEffect(() => {
    updateDashboardSessionState({ activeVacancyReportTab });
  }, [activeVacancyReportTab]);

  useEffect(() => {
    setBuiltInReportContext(null);
    if (isLMSMasterCollection) {
      const storedTab = getStoredMasterReportTab();
      setActiveMasterReportTab(
        visibleMasterReportTabs.some(([key]) => key === storedTab)
          ? storedTab
          : visibleMasterReportTabs[0]?.[0] || 'branch-report'
      );
    } else if (isLMSReconCollection) {
      const storedTab = getStoredReconReportTab();
      setActiveReconReportTab(
        visibleReconReportTabs.some(([key]) => key === storedTab)
          ? storedTab
          : visibleReconReportTabs[0]?.[0] || 'progress-report'
      );
    } else if (isDebtReviewCollection) {
      const storedTab = getStoredDebtReviewReportTab();
      setActiveDebtReviewReportTab(
        visibleDebtReviewReportTabs.some(([key]) => key === storedTab)
          ? storedTab
          : visibleDebtReviewReportTabs[0]?.[0] || 'account-access'
      );
    } else if (isVacancyReportCollection) {
      const storedTab = getStoredVacancyReportTab();
      setActiveVacancyReportTab(
        visibleVacancyReportTabs.some(([key]) => key === storedTab)
          ? storedTab
          : visibleVacancyReportTabs[0]?.[0] || 'planned-vs-actual'
      );
    } else if (isStorePerformanceCollection) {
      const storedTab = getStoredStoreReportTab();
      setActiveStoreReportTab(
        visibleStoreReportTabs.some(([key]) => key === storedTab)
          ? storedTab
          : visibleStoreReportTabs[0]?.[0] || 'store-performance'
      );
    }
  }, [activeCollectionId, isDebtReviewCollection, isLMSMasterCollection, isLMSReconCollection, isStorePerformanceCollection, isVacancyReportCollection, visibleDebtReviewReportTabs, visibleMasterReportTabs, visibleReconReportTabs, visibleStoreReportTabs, visibleVacancyReportTabs]);

  useEffect(() => {
    if (isStorePerformanceCollection && visibleStoreReportTabs.length > 0 && !visibleStoreReportTabs.some(([key]) => key === activeStoreReportTab)) {
      setActiveStoreReportTab(visibleStoreReportTabs[0][0]);
    }
    if (isLMSReconCollection && visibleReconReportTabs.length > 0 && !visibleReconReportTabs.some(([key]) => key === activeReconReportTab)) {
      setActiveReconReportTab(visibleReconReportTabs[0][0]);
    }
    if (isLMSMasterCollection && visibleMasterReportTabs.length > 0 && !visibleMasterReportTabs.some(([key]) => key === activeMasterReportTab)) {
      setActiveMasterReportTab(visibleMasterReportTabs[0][0]);
    }
    if (isDebtReviewCollection && visibleDebtReviewReportTabs.length > 0 && !visibleDebtReviewReportTabs.some(([key]) => key === activeDebtReviewReportTab)) {
      setActiveDebtReviewReportTab(visibleDebtReviewReportTabs[0][0]);
    }
    if (isVacancyReportCollection && visibleVacancyReportTabs.length > 0 && !visibleVacancyReportTabs.some(([key]) => key === activeVacancyReportTab)) {
      setActiveVacancyReportTab(visibleVacancyReportTabs[0][0]);
    }
  }, [
    activeDebtReviewReportTab,
    activeMasterReportTab,
    activeReconReportTab,
    activeStoreReportTab,
    activeVacancyReportTab,
    isDebtReviewCollection,
    isLMSMasterCollection,
    isLMSReconCollection,
    isStorePerformanceCollection,
    isVacancyReportCollection,
    visibleDebtReviewReportTabs,
    visibleMasterReportTabs,
    visibleReconReportTabs,
    visibleStoreReportTabs,
    visibleVacancyReportTabs,
  ]);

  const handleSelectCollection = (collectionId: string) => {
    const connectionId = getConnectionId();
    setActiveCollectionId(collectionId);
    updateDashboardSessionState({ activeCollectionId: collectionId });
    if (connectionId) {
      saveActiveCollectionId(collectionId, connectionId);
    }
    setQueryStatuses([]);
    setResults([]);
    resetChatSession();
  };

  const handleCreateCollection = async () => {
    const connectionId = getRequestConnectionId();
    if (!connectionId) {
      setToast({ message: "No database connection selected", type: "error" });
      return;
    }
    if (!newCollectionName.trim()) {
      setToast({ message: "Please enter a collection name", type: "warning" });
      return;
    }

    setIsCreatingCollection(true);
    try {
      const collection = await createCollection({
        connection_id: parseInt(connectionId),
        name: newCollectionName.trim(),
      });
      setCollections((prev) => [...prev, collection]);
      saveActiveCollectionId(collection.id, connectionId);
      setActiveCollectionId(collection.id);
      updateDashboardSessionState({ activeCollectionId: collection.id });
      setQueryStatuses([]);
      setResults([]);
      resetChatSession();
      setShowCreateCollectionModal(false);
      setNewCollectionName("");
      setToast({ message: "Collection created successfully", type: "success" });
    } catch (error) {
      setToast({
        message: error instanceof Error ? error.message : "Failed to create collection",
        type: "error",
      });
    } finally {
      setIsCreatingCollection(false);
    }
  };

  const loadActiveConnectionName = async () => {
    try {
      const connectionId = getConnectionId();
      if (!connectionId) {
        setActiveConnectionName(null);
        setHasValidConnection(false);
        return;
      }

      const connections = await fetchUserConnections();
      if (Array.isArray(connections)) {
        setAvailableConnections(connections);
        let activeConn = connections.find(c => c.id.toString() === connectionId);
        let shouldReloadCollections = false;
        if (!activeConn && connections.length > 0) {
          activeConn = connections[0];
          const fallbackId = activeConn.id.toString();
          saveConnectionId(fallbackId);
          shouldReloadCollections = true;
          setConnectionActive(fallbackId).catch((err: any) => console.error('Failed to set fallback connection:', err));
        }
        if (activeConn) {
          setActiveConnectionName(activeConn.connection_name || `Connection ${activeConn.id}`);
          setHasValidConnection(true);
          if (shouldReloadCollections) {
            await loadCollections();
          }
        } else {
          // Connection ID exists but is not valid
          setActiveConnectionName(null);
          setHasValidConnection(false);
        }
      } else {
        setAvailableConnections([]);
        setActiveConnectionName(null);
        setHasValidConnection(false);
      }
    } catch (error) {
      console.error("Error loading active connection name:", error);
      setAvailableConnections([]);
      setActiveConnectionName(null);
      setHasValidConnection(false);
    }
  };

  const handleQuickConnectionSelect = async (conn: any) => {
    const id = conn.id.toString();
    if (id === getConnectionId()) {
      return;
    }

    try {
      await setConnectionActive(id);
      saveConnectionId(id);
      setActiveConnectionName(conn.connection_name || `Connection ${conn.id}`);
      setQueryStatuses([]);
      setResults([]);
      resetChatSession();
      setActiveCollectionId(null);
      await loadCollections();
      setToast({ message: `Active connection changed to ${conn.connection_name || conn.id}`, type: "success" });
    } catch (error) {
      console.error("Failed to switch connection:", error);
      setToast({ message: "Failed to switch connection", type: "error" });
    }
  };

  // Refresh connection name when settings modal is closed
  useEffect(() => {
    if (!showSettings) {
      loadActiveConnectionName();
    }
  }, [showSettings]);

  // Load cached query responses from Redis — extracted as a callback so it can
  // be called on connection switch as well as on mount.
  const loadCachedResponses = useCallback(async () => {
    const connectionId = getConnectionId();
    if (!connectionId || !activeCollectionId) return;

    try {
      const cached = await fetchRecentResponses(parseInt(connectionId), activeCollectionId);
      if (cached && cached.length > 0) {
        console.log(`[Cache] Restored ${cached.length} cached query responses`);
        const cards: QueryCardData[] = cached.map((r: any) => ({
          process_id: r.process_id,
          question: r.question,
          status: r.status,
          result: r.result || undefined,
          error: r.error || undefined,
          completed_at: r.created_at ? new Date(r.created_at) : undefined,
        }));
        setQueryStatuses(cards);
      } else {
        setQueryStatuses([]);
      }
    } catch (error) {
      console.error('[Cache] Failed to load cached responses:', error);
    }
  }, [activeCollectionId]);

  // Load cached query responses from Redis on mount (1h TTL persistence)
  useEffect(() => {
    if (!hasValidConnection || !activeCollectionId) return;
    loadCachedResponses();
  }, [hasValidConnection, activeCollectionId, loadCachedResponses]);

  // Embedding status check & WebSocket connection
  useEffect(() => {
    const connectionId = getConnectionId();
    if (!connectionId) return;

    const connIdNum = parseInt(connectionId);

    // Check current embedding status via REST
    const checkStatus = async () => {
      try {
        const task = await fetchEmbeddingStatus(connIdNum);
        if (task) {
          setEmbeddingStatus(task.status);
          if (task.status === 'ERROR') {
            setEmbeddingError(task.error_message || 'Unknown error');
          } else {
            setEmbeddingError(null);
          }
        } else {
          // No task found means no embedding was ever run, or it's a legacy connection
          setEmbeddingStatus(null);
        }
      } catch (err) {
        console.error('[Embedding] Failed to fetch status:', err);
      }
    };

    checkStatus();

    // Connect WebSocket for real-time updates
    const ws = connectEmbeddingWebSocket(
      connIdNum,
      (data: EmbeddingStatusMessage) => {
        if (data.type === 'embedding_status') {
          setEmbeddingStatus(data.status);
          if (data.status === 'ERROR') {
            setEmbeddingError(data.error || data.message || 'Unknown error');
          } else {
            setEmbeddingError(null);
          }
          if (data.status === 'COMPLETED') {
            setToast({ message: 'Database embeddings are ready! You can now ask questions.', type: 'success' });
          }
        }
      },
      () => console.error('[Embedding] WebSocket error'),
      () => console.log('[Embedding] WebSocket closed')
    );
    embeddingWsRef.current = ws;

    return () => {
      ws.close();
      embeddingWsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  // Check if embedding is blocking queries
  const isEmbeddingBlocking = embeddingStatus === 'PENDING' || embeddingStatus === 'PROCESSING';

  // Auto-scroll when results change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    scrollToBottom();
  }, [results]);

  // Check for stored askMore result from Dashboard
  useEffect(() => {
    const storedResult = localStorage.getItem('askMoreResult');
    if (storedResult) {
      try {
        const parsedResult = JSON.parse(storedResult);
        const newResult: QueryResult = {
          id: Date.now().toString(),
          question: parsedResult.question,
          response: parsedResult.response,
          timestamp: new Date(parsedResult.timestamp),
        };
        setResults((prev) => [...prev, newResult]);
        localStorage.removeItem('askMoreResult');
      } catch (error) {
        console.error('Error parsing stored result:', error);
      }
    }
  }, []);

  // Handle selected prompt from history (via Dashboard navigation)
  useEffect(() => {
    const state = location.state as { selectedPrompt?: string } | null;
    if (state?.selectedPrompt) {
      setQuery(state.selectedPrompt);
      // Clear the navigation state to prevent re-triggering
      window.history.replaceState(null, '', window.location.pathname);
      // Auto-submit after a short delay
      setTimeout(() => {
        handleSubmitWithQuery(state.selectedPrompt, "");
      }, 100);
    }
  }, [location.state]);

  const handlePromptClick = (promptText: string) => {
    setQuery(promptText);
    setComment(""); // Clear any existing comment
    setShowCommentPopup(false); // Hide comment popup
    // Automatically submit the prompt
    setTimeout(() => {
      handleSubmitWithQuery(promptText, "");
    }, 100);
  };

  const handleHistoryPromptSelect = (promptText: string) => {
    setQuery(promptText);
    setComment(""); // Clear any existing comment
    setShowCommentPopup(false); // Hide comment popup
    // Automatically submit the prompt
    setTimeout(() => {
      handleSubmitWithQuery(promptText, "");
    }, 100);
  };

  const handleAskMore = (contextQuery: string, contextData?: any) => {
    setAskMorePopup({ open: true, contextQuery, contextData });
  };

  const parseAskMoreRows = (contextData?: any): any[] => {
    if (!contextData) {
      return [];
    }
    if (Array.isArray(contextData.data)) {
      return contextData.data;
    }
    if (Array.isArray(contextData.last_data_snapshot)) {
      return contextData.last_data_snapshot;
    }
    if (typeof contextData.query_response === 'string') {
      try {
        const parsed = JSON.parse(contextData.query_response);
        return Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        return [];
      }
    }
    return [];
  };

  const buildAskMoreReportContext = (
    baseContext: BuiltInReportContext | null,
    contextQuery: string,
    contextData?: any,
  ): BuiltInReportContext | undefined => {
    if (!baseContext) {
      return undefined;
    }

    if (!contextData) {
      return baseContext;
    }

    const rows = parseAskMoreRows(contextData);
    const dataSample = rows.slice(0, ASK_MORE_CONTEXT_SAMPLE_LIMIT);
    const firstRow = dataSample.find((row) => row && typeof row === 'object' && !Array.isArray(row));
    const visibleColumns = firstRow ? Object.keys(firstRow) : [];
    const numericColumns = visibleColumns.filter((column) =>
      dataSample.some((row) => {
        const value = row?.[column];
        if (value === null || value === undefined || value === '') {
          return false;
        }
        return Number.isFinite(Number(String(value).replace(/,/g, '')));
      })
    );

    const sourceContext = {
      source_type: contextData.visualization_type === 'table' || contextData.visualization_type === 'matrix'
        ? 'report_table'
        : 'static_widget',
      source_id: contextData.id,
      title: contextData.title || contextQuery,
      users_query: contextData.users_query || contextQuery,
      generated_query: contextData.generated_sql_query,
      visualization_type: contextData.visualization_type || 'table',
      report_key: (baseContext as any).active_tab,
      report_title: (baseContext as any).report_title,
      report_view: (baseContext as any).report_view,
      active_filters: {
        progress_period: (baseContext as any).progress_period,
        progress_domain: (baseContext as any).progress_domain,
        batch_filters: (baseContext as any).batch_filters,
        transaction_filters: (baseContext as any).transaction_filters,
        utility_filters: (baseContext as any).utility_filters,
        location_filters: (baseContext as any).location_filters,
        master_filters: (baseContext as any).master_filters,
      },
      row_count: contextData.source_row_count || rows.length,
      visible_columns: visibleColumns,
      numeric_columns: numericColumns,
      data_sample: dataSample,
      sample_limit: ASK_MORE_CONTEXT_SAMPLE_LIMIT,
    };

    return {
      ...(baseContext as any),
      source_context: sourceContext,
    } as BuiltInReportContext;
  };

  const closeAskMorePopup = () => {
    setAskMorePopup(null);
    setIsSubmittingAskMore(false);
  };

  const handleSubmitAskMore = async (query: string) => {
    if (!askMorePopup || !query.trim()) return;
    const connectionId = getRequestConnectionId();
    if (!connectionId) {
      setToast({ message: "No database connection selected", type: "error" });
      return;
    }
    if (!activeCollectionId) {
      setToast({ message: "Please select a collection first", type: "warning" });
      return;
    }

    setIsSubmittingAskMore(true);
    try {
      const activeSessionId = await generateSession();
      if (!activeSessionId) {
        setToast({ message: "Unable to create chat session", type: "error" });
        return;
      }

      // Build batch request with widget_id if available
      const askMoreReportContext = buildAskMoreReportContext(
        requestReportContext,
        askMorePopup.contextQuery,
        askMorePopup.contextData,
      );
      const batchRequest: any = {
        questions: [query],
        connection_id: parseInt(connectionId),
        collection_id: activeCollectionId,
        session_id: activeSessionId,
        report_context: askMoreReportContext || undefined,
        ask_more: true,
        page_session_key: pageSessionKey,
      };

      // Pass widget_id for widget follow-up context
      if (askMorePopup.contextData?.id) {
        batchRequest.widget_id = askMorePopup.contextData.id;
        console.log('[AskMore] Using widget_id:', askMorePopup.contextData.id);
      }

      const batchResponse = await submitBatchQuery(batchRequest);
      console.log('[AskMore] Created batch:', batchResponse.batch_id);

      // Add to query statuses
      const tempQueryId = `askmore-${Date.now()}`;
      setQueryStatuses(prev => [...prev, {
        process_id: tempQueryId,
        batch_id: batchResponse.batch_id,
        question: query,
        status: 'PENDING',
        statusMessage: 'Processing...',
      }]);

      // Connect to WebSocket for updates
      const ws = connectBatchWebSocket(
        batchResponse.batch_id,
        (data) => {
          console.log('[WebSocket][AskMore] Message:', data);

          if (data.type === 'result') {
            if (data.status === 'CANCELLED') {
              setQueryStatuses(prev =>
                prev.map(q =>
                  q.process_id === tempQueryId || q.process_id === data.process_id
                    ? { ...q, process_id: data.process_id || q.process_id, status: 'CANCELLED' as const, error: 'Query was cancelled.', completed_at: new Date() }
                    : q
                )
              );
              return;
            }
            setQueryStatuses(prev =>
              prev.map(q =>
                q.process_id === tempQueryId
                  ? (q.status === 'CANCELLED'
                    ? q
                    : {
                    ...q,
                    process_id: data.process_id,
                    status: 'COMPLETED' as const,
                    result: data.data,
                    completed_at: new Date(),
                    result_count: data.data?.result_count || 0,
                    pagination: data.data?.pagination ?? undefined,
                  })
                  : q
              )
            );

            // Add to results for backwards compatibility
            const newResult: QueryResult = {
              id: data.process_id,
              question: query,
              response: data.data,
              timestamp: new Date(),
            };
            setResults((prev) => [...prev, newResult]);
          } else if (data.type === 'error') {
            setQueryStatuses(prev =>
              prev.map(q =>
                q.process_id === tempQueryId
                  ? (q.status === 'CANCELLED'
                    ? q
                    : { ...q, status: 'ERROR' as const, error: data.data?.error || 'Query failed', completed_at: new Date() })
                  : q
              )
            );
          } else if (data.type === 'status_update') {
            setQueryStatuses(prev =>
              prev.map(q =>
                q.process_id === tempQueryId || q.process_id === data.process_id
                  ? (q.status === 'CANCELLED'
                    ? q
                    : {
                      ...q,
                      ...(data.data?.status === 'CANCELLED'
                        ? { status: 'CANCELLED' as const, error: data.data?.error || 'Query was cancelled.', completed_at: new Date() }
                        : { statusMessage: data.data?.message || 'Processing...' }),
                    })
                  : q
              )
            );
          } else if (data.type === 'batch_complete') {
            console.log('[WebSocket][AskMore] Batch complete');
            ws.close();
          }
        },
        (error) => console.error('[WebSocket][AskMore] Error:', error),
        () => console.log('[WebSocket][AskMore] Connection closed')
      );

      // Close popup after successful submission
      closeAskMorePopup();
    } catch (error) {
      console.error('Error submitting query:', error);
      setToast({ message: 'Failed to submit follow-up question', type: 'error' });
    } finally {
      setIsSubmittingAskMore(false);
    }
  };

  const handleSubmitWithQuery = async (
    queryText?: string,
    commentText?: string
  ) => {
    const actualQuery = queryText || query;
    const actualComment = commentText || comment;
    if (!actualQuery.trim()) return;

    const connectionId = getRequestConnectionId();
    if (!connectionId) {
      setToast({ message: "No database connection selected", type: "error" });
      return;
    }
    if (!activeCollectionId) {
      setToast({ message: "Please select a collection first", type: "warning" });
      return;
    }

    const activeSessionId = await generateSession();
    if (!activeSessionId) {
      console.error("No session ID available");
      setToast({ message: "Unable to create chat session", type: "error" });
      return;
    }

    // Save to prompt history
    PromptHistoryService.addToHistory(actualQuery, activeSessionId);
    setQuery("");
    setComment("");

    // Create temporary query status
    const tempQueryId = Date.now().toString();
    const newQueryStatus: QueryCardData = {
      process_id: tempQueryId,
      question: actualQuery,
      status: 'PENDING',
      statusMessage: 'Connecting...',
    };
    setQueryStatuses((prev) => [...prev, newQueryStatus]);

    try {
      // Submit batch query (even for single query)
      const batchResponse = await submitBatchQuery({
        questions: [actualQuery],
        connection_id: parseInt(connectionId),
        collection_id: activeCollectionId,
        session_id: activeSessionId,
        report_context: requestReportContext || undefined,
        page_session_key: pageSessionKey,
      });

      console.log('[Batch] Created batch:', batchResponse.batch_id);

      // Update status to PROCESSING immediately
      setQueryStatuses((prev) =>
        prev.map((q) =>
          q.process_id === tempQueryId ? { ...q, batch_id: batchResponse.batch_id, status: 'PROCESSING', statusMessage: 'Processing query...' } : q
        )
      );

      // Connect to WebSocket for real-time updates
      const ws = connectBatchWebSocket(
        batchResponse.batch_id,
        (data) => {
          console.log('[WebSocket] Message:', data);

          if (data.type === 'connected') {
            console.log('[WebSocket] Connected successfully');
          } else if (data.type === 'result') {
            if (data.status === 'CANCELLED') {
              setQueryStatuses((prev) =>
                prev.map((q) =>
                  q.process_id === tempQueryId || q.process_id === data.process_id
                    ? { ...q, process_id: data.process_id || q.process_id, status: 'CANCELLED', error: 'Query was cancelled.', completed_at: new Date() }
                    : q
                )
              );
              return;
            }
            // Update query status with complete result data
            setQueryStatuses((prev) =>
              prev.map((q) =>
                q.process_id === tempQueryId
                  ? (q.status === 'CANCELLED'
                    ? q
                    : {
                    ...q,
                    process_id: data.process_id,
                    status: 'COMPLETED',
                    result: data.data,
                    completed_at: new Date(),
                    result_count: data.data?.result_count || 0,
                    pagination: data.data?.pagination ?? undefined,
                  })
                  : q
              )
            );

            // Add to results for backwards compatibility
            const newResult: QueryResult = {
              id: data.process_id,
              question: actualQuery,
              response: data.data,
              timestamp: new Date(),
            };
            setResults((prev) => [...prev, newResult]);
          } else if (data.type === 'error') {
            setQueryStatuses((prev) =>
              prev.map((q) =>
                q.process_id === tempQueryId
                  ? (q.status === 'CANCELLED'
                    ? q
                    : { ...q, status: 'ERROR', error: data.data?.error, completed_at: new Date() })
                  : q
              )
            );

            const errorResult: QueryResult = {
              id: tempQueryId,
              question: actualQuery,
              response: { error: data.data?.error || 'Query failed' },
              timestamp: new Date(),
            };
            setResults((prev) => [...prev, errorResult]);
          } else if (data.type === 'status_update') {
            // Real-time processing status from backend
            setQueryStatuses((prev) =>
              prev.map((q) =>
                q.process_id === tempQueryId || q.process_id === data.process_id
                  ? (q.status === 'CANCELLED'
                    ? q
                    : {
                      ...q,
                      ...(data.data?.status === 'CANCELLED'
                        ? { status: 'CANCELLED' as const, error: data.data?.error || 'Query was cancelled.', completed_at: new Date() }
                        : { statusMessage: data.data?.message || 'Processing...' }),
                    })
                  : q
              )
            );
          } else if (data.type === 'batch_complete') {
            console.log('[WebSocket] Batch complete');
            ws.close();
          }
        },
        (error) => {
          console.error('[WebSocket] Error:', error);
          setToast({ message: 'Connection error. Please try again.', type: 'error' });
        },
        () => {
          console.log('[WebSocket] Connection closed');
        }
      );
    } catch (error) {
      console.error("Error submitting batch query:", error);
      setQueryStatuses((prev) =>
        prev.map((q) =>
          q.process_id === tempQueryId
            ? { ...q, status: 'ERROR', error: 'Failed to submit query' }
            : q
        )
      );
      setToast({ message: "Failed to submit query. Please try again.", type: "error" });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSubmitWithQuery(query, comment);
  };

  const handleDirectSubmit = async () => {
    // Direct submit without comment
    await handleSubmitWithQuery(query, comment);
    setShowCommentPopup(false);
  };

  const handleCancelComment = () => {
    setShowCommentPopup(false);
    setComment("");
  };

  const handleClearResults = () => {
    setResults([]);
  };

  const handleAskMoreAboutResult = () => {
    setShowAskMoreModal(true);
    setAskMoreQuery("");
  };

  const handleSaveWidget = () => {
    if (results.length === 0) return;
    const lastResult = results[results.length - 1];
    if (!lastResult.response || !lastResult.response.data) return;

    setWidgetName(lastResult.question);
    if (activeCollectionId?.startsWith('live-module-')) {
      // Live module: auto-assign collection + tab, no user selection needed
      setSaveCollectionId(activeCollectionId);
      setSaveReportTab(getActiveLiveReportTab());
    } else {
      setSaveCollectionId(activeCollectionId || null);
      setSaveReportTab(null);
    }
    setShowSaveModal(true);
  };

  const handleConfirmSave = async () => {
    if (!widgetName.trim()) {
      setToast({ message: "Please enter a widget name", type: "warning" });
      return;
    }

    // Determine data source: results array (populated by card save handler or inline)
    let dataToSave;

    if (results.length > 0) {
      const lastResult = results[results.length - 1];
      if (!lastResult.response || !lastResult.response.data) {
        setToast({ message: "No valid result data to save", type: "error" });
        return;
      }
      dataToSave = lastResult;
    } else {
      setToast({ message: "No result data available to save", type: "error" });
      return;
    }

    if (!saveCollectionId) {
      setToast({ message: "Please select a collection to save the widget to", type: "warning" });
      return;
    }

    const isLiveModuleSave = saveCollectionId.startsWith('live-module-');
    const selectedCollection = visibleCollections.find(c => c.id === saveCollectionId);
    if (!isLiveModuleSave && !selectedCollection) {
      setToast({ message: "Selected collection not found. Please try again.", type: "error" });
      return;
    }

    setIsSaving(true);

    try {
      const widgetPayload: any = {
        collection_id: saveCollectionId,
        connection_id: isLiveModuleSave ? 0 : selectedCollection!.connection_id,
        report_tab: saveReportTab || undefined,
        title: widgetName.trim(),
        users_query: dataToSave.question,
        generated_sql_query: dataToSave.response.query || "",
        query_response: JSON.stringify(dataToSave.response.data),
        visualization_type: dataToSave.response.visualization || "table",
        context_history: [],
      };

      const response = await authFetch(
        `${process.env.REACT_APP_API_URL}/v1/widgets/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(getAccessToken() && { Authorization: `Bearer ${getAccessToken()}` }),
          },
          body: JSON.stringify(widgetPayload),
        }
      );

      if (response.ok) {
        setShowSaveModal(false);
        setWidgetName("");

        // Remove saved response from Redis cache so it doesn't reappear
        const savedResult = results[results.length - 1];
        if (savedResult) {
          const connId = isLiveModuleSave ? 0 : selectedCollection!.connection_id;
          removeRecentResponse(connId, saveCollectionId!, savedResult.id).catch(() => {});
          setQueryStatuses(prev => prev.filter(q => q.process_id !== savedResult.id));
        }

        setResults([]); // Clear generated results

        // Refresh widgets in place without navigation
        try {
          await widgetGridRef.current?.refresh();
          setToast({ message: "Widget saved successfully!", type: "success" });
        } catch (error) {
          console.error('Error refreshing widgets:', error);
          setToast({ message: "Widget saved but failed to refresh the list. Please refresh the page.", type: "warning" });
        }
      } else {
        setToast({ message: "Failed to save widget. Please try again.", type: "error" });
      }
    } catch (error) {
      console.error("Error saving widget:", error);
      setToast({ message: "Failed to save widget. Please try again.", type: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelSave = () => {
    setShowSaveModal(false);
    setWidgetName("");
    setSaveCollectionId(null);
  };

  // Card action handlers
  const handleClearCard = (process_id: string) => {
    setQueryStatuses(prev => prev.filter(q => q.process_id !== process_id));
    setResults(prev => prev.filter(r => r.id !== process_id));

    // Remove from Redis cache so it doesn't reappear on page reload
    const connectionId = getConnectionId();
    if (connectionId) {
      if (activeCollectionId) {
        removeRecentResponse(parseInt(connectionId), activeCollectionId, process_id).catch(() => {});
      }
    }
  };

  const handleExpandCard = (query: QueryCardData) => {
    setExpandedQuery(query);
  };

  const handleCancelQuery = async (query: QueryCardData) => {
    if (!query.batch_id) {
      setToast({ message: 'Cannot cancel: missing batch id', type: 'warning' });
      return;
    }
    try {
      await cancelBatchQuery(query.batch_id);
      setQueryStatuses(prev =>
        prev.map(q =>
          q.process_id === query.process_id
            ? {
                ...q,
                status: 'CANCELLED',
                statusMessage: 'Cancelled by user',
                error: 'Query was cancelled.',
                completed_at: new Date(),
              }
            : q
        )
      );
      setToast({ message: 'Query cancelled', type: 'info' });
    } catch (error) {
      console.error('Failed to cancel query:', error);
      setToast({ message: 'Failed to cancel query', type: 'error' });
    }
  };

  const handleAskMoreFromCard = (query: QueryCardData) => {
    setAskMoreContext({
      process_id: query.process_id,
      question: query.question
    });
    setShowAskMoreModal(true);
  };

  const handleSaveWidgetFromCard = (query: QueryCardData) => {
    setResults(prev => {
      const existing = prev.find(r => r.id === query.process_id);
      if (!existing && query.result) {
        return [...prev, {
          id: query.process_id,
          question: query.question,
          response: query.result,
          timestamp: query.completed_at || new Date(),
        }];
      }
      return prev;
    });
    setWidgetName(query.question);
    if (activeCollectionId?.startsWith('live-module-')) {
      // Live module: auto-assign collection + tab so user only needs to enter a name
      setSaveCollectionId(activeCollectionId);
      setSaveReportTab(getActiveLiveReportTab());
    } else {
      setSaveCollectionId(null);
      setSaveReportTab(null);
    }
    setShowSaveModal(true);
  };

  // Retry handlers
  const handleOpenRetryModal = (query: QueryCardData) => {
    setRetryQuery(query as any);
    setShowRetryModal(true);
  };

  const handleSubmitRetry = async (editedQuestion: string) => {
    if (!retryQuery) return;

    try {
      setQueryStatuses(prev =>
        prev.filter(q => q.process_id !== retryQuery.process_id)
      );
      setShowRetryModal(false);
      setRetryQuery(null);
      await handleSubmitWithQuery(editedQuestion, '');
      setToast({ message: 'Query resubmitted successfully', type: 'success' });
    } catch (error) {
      console.error('Retry submission failed:', error);
      setToast({ message: 'Failed to resubmit query', type: 'error' });
    }
  };


  // Generate Embed Script handler
  const handleGenerateEmbedScript = async () => {
    const connectionId = getConnectionId();
    if (!connectionId) {
      setToast({ message: "No active connection selected. Please select a connection first.", type: "error" });
      return;
    }

    setIsGeneratingEmbed(true);
    try {
      const accessToken = getAccessToken();
      const response = await authFetch(
        `${process.env.REACT_APP_API_URL}/v1/embed/generate-token`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
          },
          body: JSON.stringify({ connection_id: parseInt(connectionId) }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to generate embed token`);
      }

      const data = await response.json();
      setEmbedScript(data.embed_script);
      setShowEmbedModal(true);
      setEmbedCopied(false);
    } catch (error) {
      console.error("Error generating embed script:", error);
      setToast({ message: "Failed to generate embed script", type: "error" });
    } finally {
      setIsGeneratingEmbed(false);
    }
  };

  const handleCopyEmbedScript = async () => {
    try {
      await navigator.clipboard.writeText(embedScript);
      setEmbedCopied(true);
      setToast({ message: "Script copied to clipboard!", type: "success" });
      setTimeout(() => setEmbedCopied(false), 3000);
    } catch (err) {
      console.error("Failed to copy:", err);
      setToast({ message: "Failed to copy to clipboard", type: "error" });
    }
  };

  const handleSubmitAskMoreAboutResult = async () => {
    if (!askMoreQuery.trim()) return;
    if (!activeCollectionId) {
      setToast({ message: "Please select a collection first", type: "warning" });
      return;
    }
    const connectionId = getRequestConnectionId();
    if (!connectionId) {
      setToast({ message: "No database connection selected", type: "error" });
      return;
    }

    setIsAskingMore(true);

    try {
      const activeSessionId = await generateSession();
      if (!activeSessionId) {
        setToast({ message: "Unable to create chat session", type: "error" });
        return;
      }

      // Call batch query endpoint with process_id for context
      const batchResponse = await submitBatchQuery({
        questions: [askMoreQuery],
        connection_id: parseInt(connectionId),
        collection_id: activeCollectionId!,
        session_id: activeSessionId,
        process_id: askMoreContext?.process_id,
        report_context: requestReportContext || undefined,
        ask_more: true,
        page_session_key: pageSessionKey,
      });

      // Add to query statuses
      const tempQueryId = `temp-${Date.now()}`;
      setQueryStatuses(prev => [...prev, {
        process_id: tempQueryId,
        batch_id: batchResponse.batch_id,
        question: askMoreQuery,
        status: 'PENDING',
        completed_at: undefined
      }]);

      // Connect to WebSocket for updates
      const ws = connectBatchWebSocket(
        batchResponse.batch_id,
        (data) => {
          console.log('[WebSocket][AskMore] Message:', data);

          if (data.type === 'result') {
            if (data.status === 'CANCELLED') {
              setQueryStatuses(prev =>
                prev.map(q =>
                  q.process_id === tempQueryId || q.process_id === data.process_id
                    ? { ...q, process_id: data.process_id || q.process_id, status: 'CANCELLED' as const, error: 'Query was cancelled.', completed_at: new Date() }
                    : q
                )
              );
              return;
            }
            setQueryStatuses(prev =>
              prev.map(q =>
                q.process_id === tempQueryId
                  ? (q.status === 'CANCELLED'
                    ? q
                    : {
                    ...q,
                    process_id: data.process_id,
                    status: 'COMPLETED' as const,
                    result: data.data,
                    completed_at: new Date(),
                    result_count: data.data?.result_count || 0,
                    pagination: data.data?.pagination ?? undefined,
                  })
                  : q
              )
            );
          } else if (data.type === 'error') {
            setQueryStatuses(prev =>
              prev.map(q =>
                q.process_id === tempQueryId
                  ? (q.status === 'CANCELLED'
                    ? q
                    : { ...q, status: 'ERROR' as const, error: data.data?.error || 'Query failed', completed_at: new Date() })
                  : q
              )
            );
          } else if (data.type === 'status_update') {
            // Real-time processing status from backend
            setQueryStatuses(prev =>
              prev.map(q =>
                q.process_id === tempQueryId || q.process_id === data.process_id
                  ? (q.status === 'CANCELLED'
                    ? q
                    : {
                      ...q,
                      ...(data.data?.status === 'CANCELLED'
                        ? { status: 'CANCELLED' as const, error: data.data?.error || 'Query was cancelled.', completed_at: new Date() }
                        : { statusMessage: data.data?.message || 'Processing...' }),
                    })
                  : q
              )
            );
          } else if (data.type === 'batch_complete') {
            console.log('[WebSocket][AskMore] Batch complete');
            ws.close();
          }
        },
        (error) => console.error('[WebSocket][AskMore] Error:', error),
        () => console.log('[WebSocket][AskMore] Connection closed')
      );

      // Close modal and reset
      setShowAskMoreModal(false);
      setAskMoreQuery('');
      setAskMoreContext(null);

      setToast({ message: 'Follow-up question submitted successfully', type: 'success' });

    } catch (error) {
      console.error('Ask More failed:', error);
      setToast({ message: 'Failed to submit follow-up question', type: 'error' });
    } finally {
      setIsAskingMore(false);
    }
  };

  const renderResponse = (response: any) => {
    if (!response) return <div className="no-data">Thinking...</div>;

    // Handle errors gracefully - show user-friendly message instead of raw error
    if (response.error) {
      console.error('Query response error:', response.error); // Log technical error for debugging
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="bg-slate-100 dark:bg-slate-700 rounded-full p-6 mb-4">
            <svg className="h-12 w-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <p className="text-lg font-semibold text-slate-600 dark:text-slate-300 mb-2">No Data Available</p>
          <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md">We couldn't generate results for this query. Please try rephrasing your question or contact support if the issue persists.</p>
        </div>
      );
    }

    // If there's no visualization or visualization is "text", show only summary
    const hasVisualization = response.visualization && response.visualization !== "text";

    return (
      <div className="query-result">
        {response.summary && (
          <div className="summary-section">
            <div className="summary-content">
              <div className="summary-text">{response.summary}</div>
            </div>
          </div>
        )}

        {/* Only show data visualization if there's a proper visualization type */}
        {hasVisualization && response.data && Array.isArray(response.data) && response.data.length > 0 && (
          <div className="data-section">
            <h4>Results ({response.data.length} records):</h4>
            <DataVisualizer
              data={response.data}
              visualizationType={response.visualization}
            />
          </div>
        )}

        {/* Show empty state if no data and has visualization */}
        {hasVisualization && response.data && Array.isArray(response.data) && response.data.length === 0 && (
          <div className="data-section">
            <DataVisualizer
              data={[]}
              visualizationType={response.visualization}
            />
          </div>
        )}
      </div>
    );
  };

  // UI layout with hidden header
  return (
    <div className="flex flex-col h-screen font-display bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 text-slate-700 dark:text-slate-300">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="flex flex-1 min-h-0">
        <aside className="w-72 flex-shrink-0 bg-white/85 dark:bg-slate-900/85 border-r border-slate-200 dark:border-slate-700 overflow-y-auto">
          {!hasLivePermissionProfile && (
          <div className="p-4 border-b border-slate-200 dark:border-slate-700">
            <button
              onClick={() => setShowCreateCollectionModal(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-sm transition-colors font-semibold"
            >
              <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>create_new_folder</span>
              <span>Create New Collection</span>
            </button>
          </div>
          )}
          <div className="p-3">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 px-2 mb-2">
              Collections
            </div>
            {isLoadingCollections ? (
              <div className="px-3 py-4 text-sm text-slate-500">Loading collections...</div>
            ) : collections.length === 0 ? (
              <div className="px-3 py-4 text-sm text-slate-500">No collections yet</div>
            ) : visibleCollections.length === 0 ? (
              <div className="px-3 py-4 text-sm text-slate-500">No permitted reports</div>
            ) : (
              <div className="space-y-1">
                {visibleCollections.map((collection) => (
                  <button
                    key={collection.id}
                    onClick={() => handleSelectCollection(collection.id)}
                    className={`w-full text-left px-3 py-3 rounded-lg transition-colors flex items-center gap-2 ${
                      activeCollectionId === collection.id
                        ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-semibold border-l-4 border-blue-600'
                        : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'
                    }`}
                    title={collection.name}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>folder</span>
                    <span className="truncate">{collection.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </aside>

      <main className="flex-1 flex flex-col overflow-y-auto">
        <div className="px-6 py-8">
          {/* Header with History Button */}
          <div className="flex justify-between items-center mb-8 relative">
            <div className="text-center flex-1">
              <div className="flex items-center justify-center gap-4 mb-3">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-lg">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                Rage AI
              </h1>
              <p className="text-slate-600 dark:text-slate-400">
                Ask questions in natural language and get instant data
                visualizations.
              </p>
              {/* {availableConnections.length > 0 && (
                <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                  {availableConnections.map((conn) => {
                    const isActive = activeConnectionName === conn.connection_name;
                    const dbBadge = conn.db_type === 'postgresql' ? 'PostgreSQL' : conn.db_type === 'mssql' ? 'MSSQL' : 'MySQL';
                    return (
                      <button
                        key={conn.id}
                        onClick={() => handleQuickConnectionSelect(conn)}
                        className={`inline-flex items-center px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider border shadow-sm transition-colors ${
                          isActive
                            ? 'bg-white/70 dark:bg-slate-800/70 text-blue-600 dark:text-blue-400 border-blue-200/70 dark:border-blue-800/70'
                            : 'bg-white/40 dark:bg-slate-800/40 text-slate-500 dark:text-slate-400 border-slate-200/70 dark:border-slate-700/70 hover:text-blue-600 dark:hover:text-blue-400'
                        }`}
                        type="button"
                      >
                        <span className="material-symbols-outlined mr-2" style={{ fontSize: '16px' }}>database</span>
                        <span>{conn.connection_name}</span>
                        <span className="ml-2 rounded-full bg-slate-100 dark:bg-slate-700 px-2 py-0.5 text-[10px] text-slate-500 dark:text-slate-300">
                          {dbBadge}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )} */}
              {activeCollection && (
                <div className="mt-2 inline-flex items-center px-4 py-1.5 bg-white/50 dark:bg-slate-800/50 backdrop-blur-md text-emerald-600 dark:text-emerald-400 rounded-full text-[11px] font-bold uppercase tracking-wider border border-emerald-200/50 dark:border-emerald-800/50 shadow-sm">
                  <span className="material-symbols-outlined mr-2" style={{ fontSize: '16px' }}>folder</span>
                  Collection: <span className="ml-1 text-slate-900 dark:text-slate-100">{activeCollection.name}</span>
                </div>
              )}
              {isStorePerformanceCollection && (
                <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                  {visibleStoreReportTabs.map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => setActiveStoreReportTab(key as StoreReportTab)}
                      className={`px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider border shadow-sm transition-colors ${
                        activeStoreReportTab === key
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white/40 dark:bg-slate-800/40 text-slate-500 dark:text-slate-400 border-slate-200/70 dark:border-slate-700/70 hover:text-blue-600 dark:hover:text-blue-400'
                      }`}
                      type="button"
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
              {isLMSReconCollection && (
                <div className="mt-3 flex max-h-24 flex-wrap items-center justify-center gap-2 overflow-y-auto px-2">
                  {visibleReconReportTabs.map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => setActiveReconReportTab(key)}
                      className={`px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider border shadow-sm transition-colors ${
                        activeReconReportTab === key
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white/40 dark:bg-slate-800/40 text-slate-500 dark:text-slate-400 border-slate-200/70 dark:border-slate-700/70 hover:text-blue-600 dark:hover:text-blue-400'
                      }`}
                      type="button"
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
              {isLMSMasterCollection && (
                <div className="mt-3 flex flex-wrap items-center justify-center gap-2 px-2">
                  {visibleMasterReportTabs.map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => setActiveMasterReportTab(key)}
                      className={`px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider border shadow-sm transition-colors ${
                        activeMasterReportTab === key
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white/40 dark:bg-slate-800/40 text-slate-500 dark:text-slate-400 border-slate-200/70 dark:border-slate-700/70 hover:text-blue-600 dark:hover:text-blue-400'
                      }`}
                      type="button"
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
              {isDebtReviewCollection && (
                <div className="mt-3 flex flex-wrap items-center justify-center gap-2 px-2">
                  {visibleDebtReviewReportTabs.map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => setActiveDebtReviewReportTab(key)}
                      className={`px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider border shadow-sm transition-colors ${
                        activeDebtReviewReportTab === key
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white/40 dark:bg-slate-800/40 text-slate-500 dark:text-slate-400 border-slate-200/70 dark:border-slate-700/70 hover:text-blue-600 dark:hover:text-blue-400'
                      }`}
                      type="button"
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
              {isVacancyReportCollection && (
                <div className="mt-3 flex flex-wrap items-center justify-center gap-2 px-2">
                  {visibleVacancyReportTabs.map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => setActiveVacancyReportTab(key)}
                      className={`px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider border shadow-sm transition-colors ${
                        activeVacancyReportTab === key
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white/40 dark:bg-slate-800/40 text-slate-500 dark:text-slate-400 border-slate-200/70 dark:border-slate-700/70 hover:text-blue-600 dark:hover:text-blue-400'
                      }`}
                      type="button"
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="absolute top-0 right-0 flex">
              <ThemeToggle />

              {/* Settings icon hidden
              <button
                onClick={() => setShowSettings(true)}
                className="group relative p-3 bg-white dark:bg-slate-800 rounded-2xl shadow-lg hover:shadow-blue-500/20 transition-all duration-300 transform hover:-translate-y-1 overflow-hidden"
                title="Settings"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-indigo-600/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <SvgIcon name="settings" className="relative z-10 h-6 w-6 text-slate-600 transition-colors group-hover:text-blue-600 dark:text-slate-400 dark:group-hover:text-blue-400" />
              </button>
              */}

              {/* History icon hidden
              <button
                onClick={() => setShowPromptHistory(true)}
                className="group relative p-3 bg-white dark:bg-slate-800 rounded-2xl shadow-lg hover:shadow-purple-500/20 transition-all duration-300 transform hover:-translate-y-1 overflow-hidden"
                title="View prompt history"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-pink-600/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <SvgIcon name="history" className="relative z-10 h-6 w-6 text-slate-600 transition-colors group-hover:text-purple-600 dark:text-slate-400 dark:group-hover:text-purple-400" />
              </button>
              */}

              {/* Embed icon hidden
              <button
                onClick={handleGenerateEmbedScript}
                className="group relative p-3 bg-white dark:bg-slate-800 rounded-2xl shadow-lg hover:shadow-emerald-500/20 transition-all duration-300 transform hover:-translate-y-1 overflow-hidden"
                title="Generate Embed Script"
                disabled={isGeneratingEmbed}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-teal-600/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <SvgIcon name="code" className={`relative z-10 h-6 w-6 text-slate-600 transition-colors group-hover:text-emerald-600 dark:text-slate-400 dark:group-hover:text-emerald-400 ${isGeneratingEmbed ? 'animate-pulse' : ''}`} />
              </button>
              */}

            </div>
          </div>

          <div className="mb-10 max-w-4xl mx-auto space-y-4">
            {/* No Connection Warning */}
            {!hasLivePermissionProfile && !hasValidConnection && (
              <div className="p-6 bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 border-2 border-yellow-300 dark:border-yellow-700 rounded-xl shadow-lg">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                    <span className="material-symbols-outlined text-white" style={{ fontSize: '28px' }}>
                      warning
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-yellow-900 dark:text-yellow-200 mb-2">
                      No Database Connection
                    </h3>
                    <p className="text-yellow-800 dark:text-yellow-300 mb-4">
                      You need to create a database connection first before you can ask any queries. Please configure your database connection to get started.
                    </p>
                    <button
                      onClick={() => setShowSettings(true)}
                      className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-yellow-600 to-orange-600 text-white rounded-lg hover:from-yellow-700 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 font-semibold"
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
                        settings
                      </span>
                      <span>Create Connection</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Embedding Status Banner */}
            {hasValidConnection && embeddingStatus && (embeddingStatus === 'PENDING' || embeddingStatus === 'PROCESSING') && (
              <div className="p-5 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-blue-300 dark:border-blue-700 rounded-xl shadow-lg animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center shadow-lg">
                    <span className="material-symbols-outlined text-white animate-spin" style={{ fontSize: '28px' }}>
                      sync
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-blue-900 dark:text-blue-200 mb-1">
                      Setting Up Your Database
                    </h3>
                    <p className="text-blue-700 dark:text-blue-300 text-sm">
                      We're processing your database schema to enable intelligent queries. This may take a few minutes. You'll be able to ask questions once this is complete.
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 border-4 border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>
                  </div>
                </div>
              </div>
            )}

            {/* Embedding Error Banner */}
            {hasValidConnection && embeddingStatus === 'ERROR' && (
              <div className="p-5 bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 border-2 border-red-300 dark:border-red-700 rounded-xl shadow-lg">
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-red-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                    <span className="material-symbols-outlined text-white" style={{ fontSize: '28px' }}>
                      error
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-red-900 dark:text-red-200 mb-1">
                      Database Setup Failed
                    </h3>
                    <p className="text-red-700 dark:text-red-300 text-sm">
                      Database setup failed after 3 automatic retry attempts. Please contact support or try creating a new connection.
                      {embeddingError && <span className="block mt-1 text-xs text-red-500 dark:text-red-400 truncate">Error: {embeddingError}</span>}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Main Query Input */}
            <div className="relative">
              <div className="absolute left-5 top-1/2 -translate-y-1/2 flex items-center justify-center w-10 h-10 bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-lg">
                <span className="material-symbols-outlined text-blue-600 dark:text-blue-400" style={{ fontSize: '24px' }}>
                  search
                </span>
              </div>
              <input
                className="w-full pl-20 pr-36 py-5 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-2 border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:border-blue-500 transition-all shadow-lg hover:shadow-xl text-slate-800 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder={isEmbeddingBlocking ? "Database setup in progress..." : !activeCollectionId ? "Create or select a collection first..." : "Ask anything..."}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" && !e.shiftKey && handleSubmit(e)
                }
                disabled={isLoading || isEmbeddingBlocking || !activeCollectionId}
              />
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 active:scale-95"
                type="submit"
                onClick={handleSubmit}
                disabled={!query.trim() || isEmbeddingBlocking || !activeCollectionId}
              >
                <span
                  className="material-symbols-outlined mr-2"
                  style={{ fontSize: "18px" }}
                >
                  auto_awesome
                </span>
                <span className="font-medium">Ask</span>
              </button>
            </div>
          </div>

          {/* Inline Query Result Cards - grid layout */}
          {queryStatuses.length > 0 && (
            <div className="query-results-grid" style={{ maxWidth: '100%' }}>
              {queryStatuses.map((q) => (
                <QueryResultCard
                  key={q.process_id}
                  query={q}
                  connectionName={activeConnectionName}
                  pageSessionKey={pageSessionKey}
                  onAskMore={handleAskMoreFromCard}
                  onSaveWidget={handleSaveWidgetFromCard}
                  onRetry={handleOpenRetryModal}
                  onClear={handleClearCard}
                  onExpand={handleExpandCard}
                  onCancel={handleCancelQuery}
                />
              ))}
            </div>
          )}

          {/* Combined Dashboard Section */}
          <div className="mt-12">
            <div>
              {isStorePerformanceCollection && (
                <div className="mb-8">
                  <StorePerformanceDashboard
                    activeTab={activeStoreReportTab}
                    onAskMore={handleAskMore}
                    onContextChange={setBuiltInReportContext}
                  />
                  <WidgetGrid
                    ref={widgetGridRef}
                    userId={userId}
                    collectionId={activeCollectionId}
                    reportTab={activeStoreReportTab}
                    onAskMore={handleAskMore}
                    showAll={true}
                  />
                </div>
              )}
              {isLMSReconCollection && (
                <div className="mb-8">
                  <LMSReconDashboard
                    key={activeReconReportTab}
                    activeTab={activeReconReportTab}
                    onAskMore={handleAskMore}
                    onContextChange={setBuiltInReportContext}
                  />
                  <WidgetGrid
                    ref={widgetGridRef}
                    userId={userId}
                    collectionId={activeCollectionId}
                    reportTab={activeReconReportTab}
                    onAskMore={handleAskMore}
                    showAll={true}
                  />
                </div>
              )}
              {isLMSMasterCollection && (
                <div className="mb-8">
                  <LMSMasterDashboard
                    key={activeMasterReportTab}
                    activeTab={activeMasterReportTab}
                    onAskMore={handleAskMore}
                    onContextChange={setBuiltInReportContext}
                  />
                  <WidgetGrid
                    ref={widgetGridRef}
                    userId={userId}
                    collectionId={activeCollectionId}
                    reportTab={activeMasterReportTab}
                    onAskMore={handleAskMore}
                    showAll={true}
                  />
                </div>
              )}
              {isDebtReviewCollection && (
                <div className="mb-8">
                  <DebtReviewDashboard
                    key={activeDebtReviewReportTab}
                    activeTab={activeDebtReviewReportTab}
                    onAskMore={handleAskMore}
                    onContextChange={setBuiltInReportContext}
                  />
                  <WidgetGrid
                    ref={widgetGridRef}
                    userId={userId}
                    collectionId={activeCollectionId}
                    reportTab={activeDebtReviewReportTab}
                    onAskMore={handleAskMore}
                    showAll={true}
                  />
                </div>
              )}
              {isVacancyReportCollection && (
                <div className="mb-8">
                  <VacancyReportDashboard
                    key={activeVacancyReportTab}
                    activeTab={activeVacancyReportTab}
                    onAskMore={handleAskMore}
                    onContextChange={setBuiltInReportContext}
                  />
                  <WidgetGrid
                    ref={widgetGridRef}
                    userId={userId}
                    collectionId={activeCollectionId}
                    reportTab={activeVacancyReportTab}
                    onAskMore={handleAskMore}
                    showAll={true}
                  />
                </div>
              )}

              {!hasLivePermissionProfile && (
                <div className="mb-8">
                  <WidgetGrid ref={widgetGridRef} userId={userId} collectionId={activeCollectionId} onAskMore={handleAskMore} showAll={true} />
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      </div>
      <footer className="mt-auto flex justify-between items-center px-6 py-4 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border-t-2 border-slate-200 dark:border-slate-700 text-xs text-slate-600 dark:text-slate-400">
        <span className="font-medium">Rage AI</span>
        <span>© 2026 Rage AI. All Rights Reserved.</span>
      </footer>
      {/* Save Widget Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[10000] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md border-2 border-slate-200 dark:border-slate-700">
            <div className="p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl shadow-lg">
                  <span className="material-symbols-outlined text-white" style={{ fontSize: '24px' }}>
                    bookmark_add
                  </span>
                </div>
                <h3 className="text-xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                  Save Widget
                </h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                {saveCollectionId?.startsWith('live-module-')
                  ? "Enter a name for your widget. It will be saved to this report screen."
                  : "Enter a name for your widget and choose which collection to save it to."}
              </p>
              {!saveCollectionId?.startsWith('live-module-') && (
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                    Collection
                  </label>
                  <select
                    value={saveCollectionId || ""}
                    onChange={(e) => setSaveCollectionId(e.target.value || null)}
                    disabled={isSaving}
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all shadow-sm disabled:opacity-50"
                  >
                    <option value="">Select a collection...</option>
                    {visibleCollections
                      .filter(c => !c.id.startsWith('live-module-'))
                      .map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Widget Name
                </label>
                <input
                  type="text"
                  onChange={(e) => setWidgetName(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && !isSaving && handleConfirmSave()
                  }
                  placeholder="Enter widget name..."
                  className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all shadow-sm"
                  autoFocus
                  disabled={isSaving}
                />
              </div>
              <div className="flex justify-end gap-3 mt-8">
                <button
                  onClick={handleCancelSave}
                  disabled={isSaving}
                  className="px-6 py-3 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 rounded-xl transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmSave}
                  disabled={!widgetName.trim() || !saveCollectionId || isSaving}
                  className="px-6 py-3 text-sm font-semibold bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2 shadow-lg hover:shadow-xl"
                >
                  {isSaving && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  <span>{isSaving ? "Saving..." : "Save Widget"}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Comment Popup Modal */}
      {showCommentPopup && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl border-2 border-slate-200 dark:border-slate-700">
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-lg">
                    <span className="material-symbols-outlined text-white" style={{ fontSize: '24px' }}>
                      comment
                    </span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                      Add Context to Your Query
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Provide additional context to get more accurate results
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCancelComment}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-all"
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "24px" }}
                  >
                    close
                  </span>
                </button>
              </div>

              {/* Comment Input */}
              <div className="relative mb-6">
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 block">
                  Your Query
                </label>
                <textarea
                  className="w-full px-4 py-3 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm resize-none"
                  placeholder="Add specific details, constraints, or context to help generate better results..."
                  rows={4}
                  value={comment}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setComment(e.target.value);
                    }
                  }}
                  disabled={isLoading}
                  maxLength={500}
                  autoFocus
                />
                <div
                  className={`absolute bottom-3 right-3 text-xs ${comment.length > 450
                    ? "text-red-500"
                    : comment.length > 400
                      ? "text-yellow-500"
                      : "text-slate-400"
                    }`}
                >
                  {comment.length}/500
                </div>
              </div>

              {/* Info Message */}
              {comment.trim() && (
                <div className="mb-4 text-xs text-slate-500 dark:text-slate-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                  <span className="material-symbols-outlined text-blue-500 mr-2 text-sm">
                    info
                  </span>
                  Your comment will be included with the query and previous
                  conversation context.
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={handleCancelComment}
                  disabled={isLoading}
                  className="px-6 py-3 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 rounded-xl transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDirectSubmit}
                  disabled={isLoading || !query.trim()}
                  className="px-6 py-3 text-sm font-semibold bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2 shadow-lg hover:shadow-xl"
                >
                  {isLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <span
                      className="material-symbols-outlined"
                      style={{ fontSize: "16px" }}
                    >
                      send
                    </span>
                  )}
                  <span>{isLoading ? "Submitting..." : "Submit Query"}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCreateCollectionModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10000] p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Create New Collection</h3>
              <input
                value={newCollectionName}
                onChange={(e) => setNewCollectionName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateCollection()}
                placeholder="Progress Report"
                autoFocus
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
              />
              <div className="flex justify-end gap-3 mt-5">
                <button
                  onClick={() => {
                    setShowCreateCollectionModal(false);
                    setNewCollectionName("");
                  }}
                  disabled={isCreatingCollection}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateCollection}
                  disabled={isCreatingCollection}
                  className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {isCreatingCollection ? "Creating..." : "Create"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ask More Popup Modal */}
      {askMorePopup?.open && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Ask More</h3>
              <textarea
                id="chatAskMoreTextarea"
                className="w-full h-32 p-3 border border-gray-300 dark:border-gray-700 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-800 dark:text-white"
                placeholder={askMorePopup.contextQuery || "Type your question..."}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && !isSubmittingAskMore) {
                    e.preventDefault();
                    const textarea = document.getElementById('chatAskMoreTextarea') as HTMLTextAreaElement;
                    if (textarea && textarea.value.trim()) {
                      handleSubmitAskMore(textarea.value);
                    }
                  }
                }}
              />
              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={closeAskMorePopup}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 bg-gray-200 dark:bg-gray-800 rounded-md hover:bg-gray-300 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const textarea = document.getElementById('chatAskMoreTextarea') as HTMLTextAreaElement;
                    if (textarea) {
                      handleSubmitAskMore(textarea.value);
                    }
                  }}
                  disabled={isSubmittingAskMore}
                  className="px-4 py-2 text-sm font-medium bg-primary text-white rounded-md hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSubmittingAskMore && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  {isSubmittingAskMore ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Prompt History Popup */}
      <PromptHistoryPopup
        isOpen={showPromptHistory}
        onClose={() => setShowPromptHistory(false)}
        onPromptSelect={handleHistoryPromptSelect}
        userId={userId}
      />

      {/* Settings Popup */}
      <SettingsPopup
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        onConnectionChange={() => {
          // Immediately refresh the active connection name label
          loadActiveConnectionName();
          // Clear stale query result cards from the previous connection
          setQueryStatuses([]);
          setResults([]);
          resetChatSession();
          loadCollections();
        }}
      />

      {/* Ask More Modal */}
      {showAskMoreModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl border-2 border-slate-200 dark:border-slate-700">
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-lg">
                    <span className="material-symbols-outlined text-white" style={{ fontSize: '24px' }}>
                      chat
                    </span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                      Ask More About This Result
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Ask a follow-up question based on the current result
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowAskMoreModal(false);
                    setAskMoreQuery("");
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-all"
                >
                  <span className="material-symbols-outlined" style={{ fontSize: "24px" }}>
                    close
                  </span>
                </button>
              </div>

              {/* Current Context */}
              {results.length > 0 && (
                <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                  <label className="block text-xs font-semibold text-blue-700 dark:text-blue-400 uppercase tracking-wider mb-2">
                    Current Query Context
                  </label>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    {results[results.length - 1].question}
                  </p>
                </div>
              )}

              {/* Ask More Input */}
              <div className="relative mb-6">
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 block">
                  Your Follow-up Question
                </label>
                <textarea
                  className="w-full px-4 py-3 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm resize-none"
                  placeholder="e.g., 'Can you show this by month?', 'What about last year?', 'Show me the top 5', etc."
                  rows={4}
                  value={askMoreQuery}
                  onChange={(e) => setAskMoreQuery(e.target.value)}
                  disabled={isAskingMore}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !isAskingMore) {
                      e.preventDefault();
                      handleSubmitAskMoreAboutResult();
                    }
                  }}
                />
                <div className="absolute bottom-3 right-3 text-xs text-slate-400">
                  Press Enter to submit
                </div>
              </div>

              {/* Suggested Questions */}
              <div className="mb-6">
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 block">
                  Quick Suggestions:
                </label>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setAskMoreQuery("Can you show this data in a different format?")}
                    className="text-xs px-3 py-2 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 hover:from-blue-100 hover:to-purple-100 dark:hover:from-blue-900/50 dark:hover:to-purple-900/50 border border-blue-200 dark:border-blue-800 rounded-lg transition-all hover:shadow-md transform hover:scale-105"
                    disabled={isAskingMore}
                  >
                    Different format
                  </button>
                  <button
                    onClick={() => setAskMoreQuery("What are the top 5 values?")}
                    className="text-xs px-3 py-2 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 hover:from-blue-100 hover:to-purple-100 dark:hover:from-blue-900/50 dark:hover:to-purple-900/50 border border-blue-200 dark:border-blue-800 rounded-lg transition-all hover:shadow-md transform hover:scale-105"
                    disabled={isAskingMore}
                  >
                    Top 5 values
                  </button>
                  <button
                    onClick={() => setAskMoreQuery("Can you filter this data?")}
                    className="text-xs px-3 py-2 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 hover:from-blue-100 hover:to-purple-100 dark:hover:from-blue-900/50 dark:hover:to-purple-900/50 border border-blue-200 dark:border-blue-800 rounded-lg transition-all hover:shadow-md transform hover:scale-105"
                    disabled={isAskingMore}
                  >
                    Filter data
                  </button>
                  <button
                    onClick={() => setAskMoreQuery("What insights can you provide from this?")}
                    className="text-xs px-3 py-2 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 hover:from-blue-100 hover:to-purple-100 dark:hover:from-blue-900/50 dark:hover:to-purple-900/50 border border-blue-200 dark:border-blue-800 rounded-lg transition-all hover:shadow-md transform hover:scale-105"
                    disabled={isAskingMore}
                  >
                    Insights
                  </button>
                  <button
                    onClick={() => setAskMoreQuery("Show me trends over time")}
                    className="text-xs px-3 py-2 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 hover:from-blue-100 hover:to-purple-100 dark:hover:from-blue-900/50 dark:hover:to-purple-900/50 border border-blue-200 dark:border-blue-800 rounded-lg transition-all hover:shadow-md transform hover:scale-105"
                    disabled={isAskingMore}
                  >
                    Show trends
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowAskMoreModal(false);
                    setAskMoreQuery("");
                  }}
                  disabled={isAskingMore}
                  className="px-6 py-3 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 rounded-xl transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitAskMoreAboutResult}
                  disabled={!askMoreQuery.trim() || isAskingMore}
                  className="px-6 py-3 text-sm font-semibold bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2 shadow-lg hover:shadow-xl"
                >
                  {isAskingMore ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Asking...</span>
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined" style={{ fontSize: "18px" }}>
                        send
                      </span>
                      <span>Submit</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Expand Modal */}
      {expandedQuery && (
        <QueryExpandModal
          query={expandedQuery}
          onClose={() => setExpandedQuery(null)}
          onAskMore={handleAskMoreFromCard}
          onSaveWidget={handleSaveWidgetFromCard}
          pageSessionKey={pageSessionKey}
        />
      )}

      {/* Retry Modal */}
      <RetryModal
        isOpen={showRetryModal}
        onClose={() => setShowRetryModal(false)}
        queryData={{
          question: retryQuery?.question || '',
          error: retryQuery?.error || 'Unknown error'
        }}
        onRetry={handleSubmitRetry}
      />

      {/* Ask More Modal - for query responses */}
      {showAskMoreModal && askMoreContext && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Ask More About This Query
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Previous: "{askMoreContext.question}"
              </p>
            </div>

            {/* Body */}
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Follow-up Question
              </label>
              <textarea
                value={askMoreQuery}
                onChange={(e) => setAskMoreQuery(e.target.value)}
                placeholder="e.g., Show only the top 5 results"
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
                rows={4}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && !isAskingMore) {
                    e.preventDefault();
                    handleSubmitAskMoreAboutResult();
                  }
                }}
              />
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 flex justify-end gap-3 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowAskMoreModal(false);
                  setAskMoreQuery('');
                  setAskMoreContext(null);
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                disabled={isAskingMore}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitAskMoreAboutResult}
                disabled={isAskingMore || !askMoreQuery.trim()}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isAskingMore ? (
                  <>
                    <span className="material-symbols-outlined animate-spin text-sm">refresh</span>
                    Submitting...
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined text-sm">send</span>
                    Submit
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Embed Script Modal */}
      {showEmbedModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowEmbedModal(false)}>
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                  <span className="material-symbols-outlined text-white" style={{ fontSize: '22px' }}>code</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Embed Script</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Paste this into your website</p>
                </div>
              </div>
              <button
                onClick={() => setShowEmbedModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
              >
                <span className="material-symbols-outlined text-gray-400">close</span>
              </button>
            </div>

            {/* Script */}
            <div className="flex-1 overflow-auto p-6">
              <pre className="bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 text-sm font-mono text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-all overflow-auto max-h-[50vh]">
                {embedScript}
              </pre>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowEmbedModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Close
              </button>
              <button
                onClick={handleCopyEmbedScript}
                className="px-6 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white rounded-lg transition-all flex items-center gap-2 shadow-lg"
              >
                <span className="material-symbols-outlined text-sm">{embedCopied ? 'check' : 'content_copy'}</span>
                {embedCopied ? 'Copied!' : 'Copy Script'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default ChatPage;

function scrollToBottom() {
  const chatContainer = document.getElementById("question");
  if (chatContainer) {
    chatContainer.scrollTop = chatContainer.scrollHeight;
  }
}
